import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, MessageSquare, ThumbsUp, Lock, Pin,
  Loader2, Reply, MoreVertical, Edit2, Trash2
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface Post {
  id: string;
  content: string;
  author_id: string;
  author_name: string;
  author_role: string;
  created_at: string;
  updated_at: string;
  is_edited: boolean;
  is_deleted: boolean;
  like_count: number;
  reply_to_id: string | null;
}

interface ThreadData {
  id: string;
  title: string;
  content: string;
  author_id: string;
  author_name: string;
  created_at: string;
  is_pinned: boolean;
  is_locked: boolean;
  view_count: number;
  forum_name: string;
  forum_id: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function Thread() {
  const { threadId } = useParams<{ threadId: string }>();
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [thread, setThread] = useState<ThreadData | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    loadThread(1);
  }, [threadId]);

  async function loadThread(page: number) {
    try {
      setLoading(true);
      const data = await fetchApi<{
        thread: ThreadData;
        posts: Post[];
        pagination: Pagination;
      }>(`/api/discussions/threads/${threadId}?page=${page}`);
      setThread(data.thread);
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to load thread');
    } finally {
      setLoading(false);
    }
  }

  async function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyContent.trim() || !user) return;

    setSubmitting(true);
    setError(null);

    try {
      await fetchApi(`/api/discussions/threads/${threadId}/posts`, {
        method: 'POST',
        body: JSON.stringify({
          content: replyContent,
          reply_to_id: replyingTo
        })
      });
      setReplyContent('');
      setReplyingTo(null);
      loadThread(pagination?.page || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLike(postId: string) {
    if (!user) return;

    try {
      await fetchApi(`/api/discussions/posts/${postId}/like`, { method: 'POST' });
      // Optimistic update
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, like_count: p.like_count + 1 }
          : p
      ));
    } catch (err) {
      console.error('Failed to like post:', err);
    }
  }

  async function handleEdit(postId: string) {
    if (!editContent.trim()) return;

    setSubmitting(true);
    try {
      await fetchApi(`/api/discussions/posts/${postId}`, {
        method: 'PUT',
        body: JSON.stringify({ content: editContent })
      });
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, content: editContent, is_edited: true }
          : p
      ));
      setEditingPost(null);
      setEditContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit post');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(postId: string) {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      await fetchApi(`/api/discussions/posts/${postId}`, { method: 'DELETE' });
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, content: '[deleted]', is_deleted: true }
          : p
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    }
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading && !thread) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error && !thread) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <p className="text-red-400">{error}</p>
          <Link to="/community" className="text-accent-blue hover:underline mt-2 inline-block">
            ← Back to Community
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link
        to={`/forums/${thread?.forum_id}`}
        className="inline-flex items-center gap-1 text-gray-400 hover:text-white mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to {thread?.forum_name}
      </Link>

      {/* Thread Header */}
      <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-2 mb-2">
          {thread?.is_pinned && (
            <Pin className="w-5 h-5 text-accent-blue flex-shrink-0" />
          )}
          {thread?.is_locked && (
            <Lock className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          )}
          <h1 className="text-2xl font-bold text-white">{thread?.title}</h1>
        </div>

        <div className="prose text-gray-300 mb-4">
          <p>{thread?.content}</p>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>Posted by {thread?.author_name}</span>
          <span>•</span>
          <span>{thread && formatDate(thread.created_at)}</span>
          <span>•</span>
          <span>{thread?.view_count} views</span>
        </div>
      </div>

      {/* Posts */}
      <div className="space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-white">
          <MessageSquare className="w-5 h-5 inline mr-2" />
          Replies ({pagination?.total || 0})
        </h2>

        {posts.length === 0 ? (
          <div className="bg-gray-800/50 rounded-lg p-8 text-center text-gray-400">
            <p>No replies yet. Be the first to respond!</p>
          </div>
        ) : (
          posts.map(post => (
            <div
              key={post.id}
              className={`bg-gray-800/50 rounded-lg p-4 ${post.is_deleted ? 'opacity-60' : ''}`}
            >
              {editingPost === post.id ? (
                <div>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-accent-blue focus:outline-none resize-none"
                    rows={4}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => setEditingPost(null)}
                      className="px-3 py-1 text-gray-400 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleEdit(post.id)}
                      disabled={submitting}
                      className="px-3 py-1 bg-accent-blue hover:bg-blue-500 text-slate-900 rounded"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-400">
                        {post.author_name.charAt(0)}
                      </div>
                      <div>
                        <span className="text-white font-medium">{post.author_name}</span>
                        {post.author_role !== 'user' && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-accent-blue/20 text-accent-blue rounded">
                            {post.author_role}
                          </span>
                        )}
                      </div>
                    </div>

                    {user && (user.id === post.author_id || user.role === 'admin') && !post.is_deleted && (
                      <div className="relative group">
                        <button className="p-1 text-gray-500 hover:text-white">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button
                            onClick={() => {
                              setEditingPost(post.id);
                              setEditContent(post.content);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {post.reply_to_id && (
                    <div className="text-sm text-gray-500 mb-2 pl-2 border-l-2 border-gray-700">
                      Replying to a post
                    </div>
                  )}

                  <p className="text-gray-300 mb-3">{post.content}</p>

                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">
                      {formatDate(post.created_at)}
                      {post.is_edited && ' (edited)'}
                    </span>

                    {!post.is_deleted && (
                      <>
                        <button
                          onClick={() => handleLike(post.id)}
                          disabled={!user}
                          className="flex items-center gap-1 text-gray-500 hover:text-accent-blue transition-colors"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          {post.like_count > 0 && post.like_count}
                        </button>

                        {user && !thread?.is_locked && (
                          <button
                            onClick={() => setReplyingTo(post.id)}
                            className="flex items-center gap-1 text-gray-500 hover:text-white transition-colors"
                          >
                            <Reply className="w-4 h-4" />
                            Reply
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mb-8">
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => loadThread(page)}
              className={`
                w-8 h-8 rounded-lg transition-colors
                ${page === pagination.page
                  ? 'bg-accent-blue text-slate-900'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }
              `}
            >
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Reply Form */}
      {user && !thread?.is_locked ? (
        <form onSubmit={handleReply} className="bg-gray-800/50 rounded-lg p-4">
          {replyingTo && (
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-gray-400">Replying to a post</span>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                className="text-gray-500 hover:text-white"
              >
                Cancel
              </button>
            </div>
          )}

          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write your reply..."
            rows={4}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:outline-none resize-none"
          />

          {error && (
            <p className="text-red-400 text-sm mt-2">{error}</p>
          )}

          <div className="flex justify-end mt-3">
            <button
              type="submit"
              disabled={submitting || !replyContent.trim()}
              className="px-6 py-2 bg-accent-blue hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 rounded-lg transition-colors"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Post Reply'
              )}
            </button>
          </div>
        </form>
      ) : thread?.is_locked ? (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
          <Lock className="w-5 h-5 inline mr-2 text-yellow-500" />
          <span className="text-yellow-500">This thread is locked and no longer accepting replies.</span>
        </div>
      ) : (
        <div className="bg-gray-800/50 rounded-lg p-4 text-center">
          <Link to="/login" className="text-accent-blue hover:underline">
            Log in to reply
          </Link>
        </div>
      )}
    </div>
  );
}
